import clsx from "clsx";
import React, { FunctionComponent, ReactNode } from "react";

import { useUpgradeToken } from "../../api-client/upgradeToken";
import { config } from "../../config/config";
import { useI18n } from "../../i18n/useI18n";
import {
  DealerComparePrintRoute,
  DealerHomePageRoute,
  DealerLandingPageRoute,
  DealerOrderPrintRoute,
  DealerPartNumberResultsRoute,
  DealerTiresizeResultsRoute,
  DealerVehicleResultsRoute,
  DealerVehicleResultsWithoutTrimRoute,
  DealerWishlistPrintRoute,
} from "../../navigation/DealerRoutes";
import {
  DealerGroupLandingPageRoute,
  NationalTiresizeResultsRoute,
  NationalVehicleResultsRoute,
  NationalVendorLandingRoute,
} from "../../navigation/NationalRoutes";
import { useScrollRestoration } from "../../navigation/useScrollRestoration";
import { useSomeRoutes } from "../../navigation/useSomeRoutes";
import { useNullableDealerAccountInfo } from "../../stores/hooks/useNullableDealerAccountInfo";
import { useSearchFormSync } from "../../stores/hooks/useSearchFormSync";
import { useUrlSync } from "../../stores/hooks/useUrlSync";
import { useBodyClass } from "../../util/useBodyClass";
import { useGoogleTagManager } from "../../util/useGoogleTagManager";
import { ReCaptcha } from "../Captcha/ReCaptcha";
import { DealerOrderModal } from "../Cart/DealerOrderModal/DealerOrderModal";
import { Header } from "../Header/Header";
import { SearchFormResults } from "../SearchForm/SearchFormResults";
import { Footer } from "./Footer";
import { LegalFooter } from "./LegalFooter";
import { useDealerIdFixup } from "./useDealerIdFixup";

type DealerLayoutProps = {
  children: ReactNode;
};

export const Layout: FunctionComponent<DealerLayoutProps> = ({ children }) => {
  useBodyClass(config.themeClass);
  useSearchFormSync();
  useI18n();
  useScrollRestoration();
  useUrlSync();
  useUpgradeToken();
  useGoogleTagManager();
  useDealerIdFixup();

  const dealer = useNullableDealerAccountInfo();

  const isHomePage = useSomeRoutes(
    DealerHomePageRoute,
    DealerLandingPageRoute,
    NationalVendorLandingRoute,
    DealerGroupLandingPageRoute
  );

  const sholdRenderSearchForm = useSomeRoutes(
    DealerLandingPageRoute,
    DealerVehicleResultsWithoutTrimRoute,
    DealerVehicleResultsRoute,
    DealerTiresizeResultsRoute,
    DealerPartNumberResultsRoute,
    NationalVendorLandingRoute,
    NationalVehicleResultsRoute,
    NationalTiresizeResultsRoute,
    DealerGroupLandingPageRoute
  );

  const isPrint = useSomeRoutes(
    DealerWishlistPrintRoute,
    DealerOrderPrintRoute,
    DealerComparePrintRoute
  );

  if (isPrint) {
    return <div className="tw-layout bg-slate-300">{children}</div>;
  }

  const isMazda = dealer?.dealerNationalAccountName === "Mazda";

  const shouldRenderFooter = isMazda ? isHomePage : true;

  return (
    <div className="tw-layout">
      <Header />
      {sholdRenderSearchForm && (
        <SearchFormResults className={clsx(!isHomePage && "shadow-xl")} />
      )}
      <main className={clsx("w-full pb-8", !isHomePage && "px-4")}>
        <div className={clsx(!isHomePage && "max-w-page mx-auto")}>
          {children}
        </div>
      </main>
      {shouldRenderFooter && <Footer />}
      <LegalFooter />
      <DealerOrderModal />
      <ReCaptcha />
    </div>
  );
};
